import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Buildings, Lock, Envelope } from '@phosphor-icons/react';

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('تم تسجيل الدخول بنجاح');
      nav('/');
    } catch (err) {
      toast.error('بيانات الدخول غير صحيحة');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left / Hero */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/36769041/pexels-photo-36769041.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1200"
          alt="Riyadh Business District"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 login-overlay" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#B4935A] flex items-center justify-center rounded-md">
              <Buildings weight="bold" className="text-white" size={22} />
            </div>
            <div>
              <div className="text-lg font-bold">إدارة التحصيل</div>
              <div className="text-xs text-white/70">Collection Management</div>
            </div>
          </div>
          <div className="space-y-4 max-w-xl">
            <h1 className="text-4xl font-bold leading-[1.4]">منصة إدارة التحصيل<br/>ومتابعة المستحقات</h1>
            <p className="text-white/80 text-base leading-[1.9]">
              إدارة التحصيل هي الذراع التنفيذية المسؤولة عن تحصيل مستحقات الشركة، ومتابعة
              الفواتير الصادرة من نظام Yardi، وضمان الالتزام بمواعيد السداد وتقليل المتعثرات.
            </p>
            <div className="pt-2 space-y-2">
              <div className="text-xs uppercase tracking-widest text-[#D4B683]">مهامنا</div>
              <ul className="text-sm text-white/80 space-y-1.5 leading-[1.9]">
                <li>• متابعة الفواتير المستحقة وتحصيلها في مواعيدها</li>
                <li>• التواصل مع المستأجرين وتنظيم إجراءات المتابعة</li>
                <li>• إصدار الخطابات والإنذارات الرسمية</li>
                <li>• رفع التقارير الدورية للإدارة العليا</li>
                <li>• إحالة الحالات المتعثرة قانونياً أو مالياً</li>
              </ul>
            </div>
            <div className="pt-2 space-y-2">
              <div className="text-xs uppercase tracking-widest text-[#D4B683]">أهدافنا</div>
              <ul className="text-sm text-white/80 space-y-1.5 leading-[1.9]">
                <li>• رفع نسبة التحصيل وتقليل المبالغ المتأخرة</li>
                <li>• أتمتة العمليات وتقليل العمل اليدوي</li>
                <li>• تحسين تجربة المستأجرين مع الالتزام النظامي</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Right / Form */}
      <div className="flex items-center justify-center p-8 bg-[#F8F9FA]">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-2">Welcome back</div>
            <h2 className="text-3xl font-bold text-slate-900">تسجيل الدخول</h2>
            <p className="text-sm text-slate-500 mt-2">ادخل بياناتك للوصول إلى منصة التحصيل</p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-700 mb-2 block">البريد الإلكتروني</Label>
              <div className="relative">
                <Envelope className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pr-10 h-11"
                  placeholder="name@company.com"
                  autoComplete="username"
                  data-testid="login-email-input"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700 mb-2 block">كلمة المرور</Label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10 h-11"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-base"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'جاري الدخول...' : 'دخول'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
